import React, { useState, useEffect } from 'react';
import { Button, Input, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Badge, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import { X, Users, Crown, Shield, MoreVertical, UserPlus, Edit3 } from 'lucide-react';

const ConversationSettings = ({ 
  conversation, 
  currentUser, 
  onClose, 
  onLeaveConversation, 
  onUpdateConversation 
}) => {
  const [title, setTitle] = useState(conversation.title || '');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserSearch, setNewUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localUsers, setLocalUsers] = useState(conversation.users || []);

  const isGroupConversation = conversation.users?.length > 2;
  const currentUserRole = getCurrentUserRole();
  const isAdmin = currentUserRole === 'Admin' || currentUserRole === 'SuperAdmin';
  const isSuperAdmin = currentUserRole === 'SuperAdmin';

  function getCurrentUserRole() {
    const currentUserInConv = conversation.users?.find(u => u.username === currentUser.username);
    return currentUserInConv?.conversation_user?.conversation_role?.name;
  }

  function getUserRole(user) {
    return user?.conversation_user?.conversation_role?.name;
  }

  const handleTitleUpdate = async () => {
    if (title.trim() !== conversation.title) {
      setLoading(true);
      try {
        await onUpdateConversation({ title: title.trim() });
        setIsEditingTitle(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMakeAdmin = async (conversationUserId) => {
    if (!isSuperAdmin) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/conversations/${conversation._id}/admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ conversation_user_id: conversationUserId })
      });
      
      if (response.ok) {
        // Update local state immediately for real-time UI update
        setLocalUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.conversation_user?._id === conversationUserId || user.conversation_user?.id === conversationUserId) {
              return {
                ...user,
                conversation_user: {
                  ...user.conversation_user,
                  conversation_role: { name: 'Admin' }
                }
              };
            }
            return user;
          })
        );
        
        // Also trigger parent refresh in background
        if (onUpdateConversation) {
          onUpdateConversation({ forceRefresh: true });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (conversationUserId) => {
    if (!isSuperAdmin) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/conversations/${conversation._id}/admin`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ conversation_user_id: conversationUserId })
      });
      
      if (response.ok) {
        // Update local state immediately for real-time UI update
        setLocalUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.conversation_user?._id === conversationUserId || user.conversation_user?.id === conversationUserId) {
              return {
                ...user,
                conversation_user: {
                  ...user.conversation_user,
                  conversation_role: null
                }
              };
            }
            return user;
          })
        );
        
        // Also trigger parent refresh in background
        if (onUpdateConversation) {
          onUpdateConversation({ forceRefresh: true });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userToRemove) => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const remainingUsers = conversation.users
        .filter(u => u.username !== userToRemove.username)
        .map(u => u.username);
      
      await onUpdateConversation({ neighbors: remainingUsers });
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`/api/v1/users/search?q=${encodeURIComponent(query)}&per_page=10`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter out users already in conversation
        const existingUsernames = conversation.users.map(u => u.username);
        const filteredResults = data.users.filter(u => !existingUsernames.includes(u.username));
        setSearchResults(filteredResults);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleAddUser = async (userToAdd) => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const newUsers = [...conversation.users.map(u => u.username), userToAdd.username];
      await onUpdateConversation({ neighbors: newUsers });
      setShowAddUser(false);
      setNewUserSearch('');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(newUserSearch);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [newUserSearch]);

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'Admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return <Badge color="warning" size="sm">Owner</Badge>;
      case 'Admin':
        return <Badge color="primary" size="sm">Admin</Badge>;
      default:
        return null;
    }
  };

  return (
    <Modal 
      isOpen={true} 
      onClose={onClose} 
      size="2xl" 
      placement="center"
      backdrop="opaque"
      classNames={{
        backdrop: "bg-black/50 z-[9998]",
        wrapper: "z-[9999]",
        base: "z-[9999]"
      }}
    >
      <ModalContent className="bg-white shadow-2xl">
        <ModalHeader className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          {isGroupConversation ? 'Group Settings' : 'Conversation Settings'}
        </ModalHeader>
        
        <ModalBody className="space-y-6">
          {/* Group Title */}
          {isGroupConversation && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Group Name</label>
              {isEditingTitle ? (
                <div className="flex gap-2">
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter group name"
                    disabled={loading}
                  />
                  <Button
                    size="sm"
                    color="primary"
                    onPress={handleTitleUpdate}
                    isLoading={loading}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setTitle(conversation.title || '');
                      setIsEditingTitle(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">{conversation.title || 'Unnamed Group'}</span>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<Edit3 className="w-4 h-4" />}
                      onPress={() => setIsEditingTitle(true)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Members List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Members ({localUsers?.length || 0})
              </label>
              {isAdmin && isGroupConversation && (
                <Button
                  size="sm"
                  color="primary"
                  variant="light"
                  startContent={<UserPlus className="w-4 h-4" />}
                  onPress={() => setShowAddUser(true)}
                >
                  Add Member
                </Button>
              )}
            </div>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {localUsers?.map((user) => {
                const userRole = getUserRole(user);
                const isCurrentUser = user.username === currentUser.username;
                const conversationUserId = user.conversation_user?._id || user.conversation_user?.id;
                
                console.log('User:', user.username, 'Role:', userRole, 'ConversationUserId:', conversationUserId, 'IsAdmin:', isAdmin, 'IsSuperAdmin:', isSuperAdmin);
                
                return (
                  <div key={user.username} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.username}</span>
                          {isCurrentUser && <span className="text-xs text-gray-500">(You)</span>}
                          {getRoleIcon(userRole)}
                        </div>
                        <div className="flex items-center gap-2">
                          {user.first_name && user.last_name && (
                            <span className="text-sm text-gray-600">{user.first_name} {user.last_name}</span>
                          )}
                          {getRoleBadge(userRole)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Admin Actions */}
                    {isGroupConversation && !isCurrentUser && (isSuperAdmin || (isAdmin && userRole !== 'SuperAdmin')) && conversationUserId && (
                      <Dropdown 
                        classNames={{
                          content: "bg-white shadow-lg border border-gray-200 rounded-lg p-1"
                        }}
                      >
                        <DropdownTrigger>
                          <Button isIconOnly size="sm" variant="light">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                          classNames={{
                            base: "bg-white",
                            list: "bg-white"
                          }}
                        >
                          {isSuperAdmin && userRole !== 'Admin' && userRole !== 'SuperAdmin' && (
                            <DropdownItem
                              key="make-admin"
                              startContent={<Shield className="w-4 h-4" />}
                              onPress={() => handleMakeAdmin(conversationUserId)}
                            >
                              Make Admin
                            </DropdownItem>
                          )}
                          {isSuperAdmin && userRole === 'Admin' && (
                            <DropdownItem
                              key="remove-admin"
                              startContent={<Shield className="w-4 h-4" />}
                              onPress={() => handleRemoveAdmin(conversationUserId)}
                            >
                              Remove Admin
                            </DropdownItem>
                          )}
                          <DropdownItem
                            key="remove"
                            className="text-danger"
                            color="danger"
                            startContent={<X className="w-4 h-4" />}
                            onPress={() => handleRemoveUser(user)}
                          >
                            Remove from Group
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    )}
                    
                    {/* Debug info for admins */}
                    {isCurrentUser && (
                      <div className="text-xs text-gray-400">
                        Role: {currentUserRole || 'None'} | Admin: {isAdmin ? 'Yes' : 'No'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add User Modal */}
          {showAddUser && (
            <div className="space-y-3 p-4 border rounded-lg bg-blue-50">
              <h4 className="font-medium">Add New Member</h4>
              <Input
                placeholder="Search users..."
                value={newUserSearch}
                onChange={(e) => setNewUserSearch(e.target.value)}
              />
              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {searchResults.map((user) => (
                    <div key={user.username} className="flex items-center justify-between p-2 bg-white rounded">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm">{user.username}</span>
                      </div>
                      <Button
                        size="sm"
                        color="primary"
                        onPress={() => handleAddUser(user)}
                        isLoading={loading}
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button
                size="sm"
                variant="light"
                onPress={() => {
                  setShowAddUser(false);
                  setNewUserSearch('');
                  setSearchResults([]);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            Close
          </Button>
          <Button
            color="danger"
            variant="light"
            onPress={onLeaveConversation}
          >
            Leave {isGroupConversation ? 'Group' : 'Conversation'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConversationSettings;
